import { useEffect, useRef, useState } from "react";
import api, { fmtIDR } from "@/lib_app/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Avatar, { fileToAvatarDataURL } from "@/components/app/Avatar";
import CompanySettings from "@/components/app/CompanySettings";
import DateInput from "@/components/app/DateInput";
import {
  Upload, Trash2, Save, Mail, Phone, Linkedin, MapPin, Calendar, Globe,
  Briefcase, Users2, User, TrendingUp, Award, Target as TargetIcon, Trophy, Pencil, Building2, ImagePlus, X,
} from "lucide-react";

const ROLE_LABEL = { owner: "Owner", sales_director: "Sales Director", sales: "Sales" };
const GENDER_OPTIONS = [
  { v: "male", label: "Laki-laki" },
  { v: "female", label: "Perempuan" },
  { v: "other", label: "Lainnya" },
];
const EMPLOYMENT_OPTIONS = [
  { v: "full_time", label: "Full-time" },
  { v: "contract", label: "Kontrak" },
  { v: "intern", label: "Intern" },
];

const staffIdFor = (u) => {
  if (!u) return "";
  const y = (u.join_date || u.created_at || "").slice(0, 4) || new Date().getFullYear();
  const suffix = (u.user_id || "").slice(-6).toUpperCase();
  const prefix = u.role === "owner" ? "OWN" : u.role === "sales_director" ? "DIR" : "SLS";
  return `${prefix}-${y}-${suffix}`;
};

const fmtDate = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  } catch { return s; }
};

const monthLabel = (mk) => {
  if (!mk) return "-";
  const [y, m] = mk.split("-");
  const names = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
  return `${names[Number(m) - 1] || m} ${y}`;
};

export default function Profile({ embedded = false, hideHighlights = false, hideCompany = false }) {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [highlights, setHighlights] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [personalOpen, setPersonalOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const fileRef = useRef();

  const load = async () => {
    if (!user) return;
    try {
      const [p, h, u] = await Promise.all([
        api.get(`/users/${user.user_id}/profile`),
        api.get(`/users/${user.user_id}/sales_highlights`),
        api.get("/users"),
      ]);
      setProfile(p.data);
      setHighlights(h.data);
      setUsers(u.data);
      setDraft(p.data);
    } catch (err) {
      toast.error("Gagal memuat profil");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.user_id]);

  const pickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus gambar");
    try {
      const dataUrl = await fileToAvatarDataURL(file);
      setBusy(true);
      await api.post(`/users/${user.user_id}/avatar`, { picture: dataUrl });
      setProfile((p) => ({ ...p, picture: dataUrl }));
      await refresh();
      toast.success("Foto profil diperbarui");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload");
    } finally { setBusy(false); }
  };
  const removeAvatar = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${user.user_id}/avatar`, { picture: "" });
      setProfile((p) => ({ ...p, picture: "" }));
      await refresh();
      toast.success("Foto profil dihapus");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const savePersonal = async () => {
    setBusy(true);
    try {
      const payload = {
        name: (draft.name || "").trim() || profile.name,
        nickname: draft.nickname || "",
        date_of_birth: draft.date_of_birth || null,
        gender: draft.gender || "",
        city: draft.city || "",
        language: draft.language || "",
      };
      const { data } = await api.patch("/users/me/profile", payload);
      setProfile(data);
      setDraft(data);
      setPersonalOpen(false);
      await refresh();
      toast.success("Info pribadi diperbarui");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const saveContact = async () => {
    setBusy(true);
    try {
      const payload = { phone: draft.phone || "", linkedin: draft.linkedin || "", alt_email: draft.alt_email || "" };
      const { data } = await api.patch("/users/me/profile", payload);
      setProfile(data);
      setDraft(data);
      setContactOpen(false);
      toast.success("Kontak diperbarui");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const saveWork = async () => {
    setBusy(true);
    try {
      const payload = {
        job_title: draft.job_title || "",
        team: draft.team || "",
        manager_id: draft.manager_id || null,
        employment_type: draft.employment_type || "",
        work_area: draft.work_area || "",
        join_date: draft.join_date || null,
      };
      const { data } = await api.patch(`/users/${profile.user_id}/work`, payload);
      setProfile(data);
      setDraft(data);
      setWorkOpen(false);
      toast.success("Info pekerjaan diperbarui");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal (butuh hak owner/director)");
    } finally { setBusy(false); }
  };

  if (!profile) return <div className="text-sm text-[#A0A3A8]">Memuat...</div>;

  const canEditWork = user?.role === "owner" || user?.role === "sales_director";
  const manager = users.find((u) => u.user_id === profile.manager_id);
  const waHref = profile.phone ? `https://wa.me/${profile.phone.replace(/\D/g, "").replace(/^0/, "62")}` : null;

  return (
    <div className="space-y-6" data-testid="profile-page">
      {!embedded && (
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Akun</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Profil Saya</h1>
        </div>
      )}

      {/* Header Card */}
      <Card className="p-6 md:p-7 border-0 rounded-2xl card-soft" data-testid="profile-header">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className="relative">
            <Avatar name={profile.name} picture={profile.picture} size="lg" className="w-24 h-24 text-2xl ring-4 ring-white shadow-md" />
            <button
              onClick={pickFile}
              disabled={busy}
              data-testid="avatar-upload-btn"
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#090808] hover:bg-[#000000] text-white flex items-center justify-center shadow-md transition-colors"
              title="Unggah foto"
            >
              <Upload className="w-4 h-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="avatar-file-input" />
          </div>
          <div className="flex-1 min-w-0 text-center md:text-left">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
              <h2 className="text-2xl font-bold text-[#090808]">{profile.name}</h2>
              <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-[#DCE0E7] text-[#090808] text-[11px] font-bold">
                {ROLE_LABEL[profile.role] || profile.role}
              </span>
              {profile.is_active ? (
                <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-[#C9F0D6] text-[#4A6D53] text-[11px] font-bold">Aktif</span>
              ) : (
                <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-[#DCE0E7] text-[#626061] text-[11px] font-bold">Nonaktif</span>
              )}
            </div>
            <div className="mt-1 text-sm text-[#A0A3A8]">
              {profile.job_title || "—"} {profile.team && <span>· {profile.team}</span>}
            </div>
            {profile.picture && (
              <button onClick={removeAvatar} disabled={busy} data-testid="avatar-remove-btn" className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#A0A3A8] hover:text-[#FF6B7D] transition-colors">
                <Trash2 className="w-3 h-3" /> Hapus foto
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 md:gap-6 md:border-l md:border-[#DCE0E7] md:pl-6 shrink-0 text-sm">
            <MetaField icon={User} label="Staff ID" value={staffIdFor(profile)} />
            <MetaField icon={Calendar} label="Bergabung" value={fmtDate(profile.join_date || profile.created_at)} />
            <MetaField
              icon={Phone}
              label="WhatsApp"
              value={profile.phone || "—"}
              href={waHref}
            />
            <MetaField icon={Mail} label="Email" value={profile.email} href={`mailto:${profile.email}`} />
          </div>
        </div>
      </Card>

      {/* Sales Highlights */}
      {highlights && !hideHighlights && (
        <Card className="p-6 border-0 rounded-2xl card-soft" data-testid="sales-highlights">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl bg-[#C9F0D6] text-[#57D28A] flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div className="text-sm font-bold text-[#090808]">Sales Highlights</div>
            <div className="text-xs text-[#A0A3A8]">Rekam jejak lifetime</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat icon={Users2} label="Total Prospek" value={highlights.total_prospek} />
            <Stat icon={Award} label="Total Deal" value={highlights.total_deal} tint="emerald" />
            <Stat icon={TrendingUp} label="Win Rate" value={`${highlights.win_rate}%`} tint="sky" />
            <Stat icon={Trophy} label="Bulan Terbaik" value={monthLabel(highlights.best_month)} sub={highlights.best_month_value ? fmtIDR(highlights.best_month_value) : ""} tint="amber" />
            <Stat icon={TargetIcon} label={`Achievement ${new Date().getFullYear()}`} value={`${highlights.achievement_pct}%`} sub={fmtIDR(highlights.ytd_realization)} tint="violet" />
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Info */}
        <Card className="p-6 border-0 rounded-2xl card-soft" data-testid="personal-info">
          <SectionHead
            icon={User} title="Info Pribadi"
            onEdit={() => { setDraft(profile); setPersonalOpen((v) => !v); }}
            editing={personalOpen}
          />
          {!personalOpen ? (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <ReadField label="Nama Lengkap" value={profile.name} />
              <ReadField label="Nama Panggilan" value={profile.nickname} />
              <ReadField label="Tanggal Lahir" value={fmtDate(profile.date_of_birth)} />
              <ReadField label="Gender" value={(GENDER_OPTIONS.find((g) => g.v === profile.gender) || {}).label} />
              <ReadField label="Kota" value={profile.city} icon={MapPin} />
              <ReadField label="Bahasa" value={profile.language} icon={Globe} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <FormField label="Nama Lengkap"><Input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></FormField>
              <FormField label="Nama Panggilan"><Input value={draft.nickname || ""} onChange={(e) => setDraft({ ...draft, nickname: e.target.value })} /></FormField>
              <FormField label="Tanggal Lahir"><DateInput value={draft.date_of_birth || ""} onChange={(e) => setDraft({ ...draft, date_of_birth: e.target.value })} /></FormField>
              <FormField label="Gender">
                <Select value={draft.gender || ""} onValueChange={(v) => setDraft({ ...draft, gender: v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{GENDER_OPTIONS.map((g) => <SelectItem key={g.v} value={g.v}>{g.label}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
              <FormField label="Kota"><Input value={draft.city || ""} onChange={(e) => setDraft({ ...draft, city: e.target.value })} /></FormField>
              <FormField label="Bahasa"><Input value={draft.language || ""} onChange={(e) => setDraft({ ...draft, language: e.target.value })} placeholder="Indonesia, English…" /></FormField>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setPersonalOpen(false); setDraft(profile); }}>Batal</Button>
                <Button onClick={savePersonal} disabled={busy} className="bg-[#090808] hover:bg-[#000000] gap-1.5"><Save className="w-3.5 h-3.5" /> Simpan</Button>
              </div>
            </div>
          )}
        </Card>

        {/* Contact & Social */}
        <Card className="p-6 border-0 rounded-2xl card-soft" data-testid="contact-info">
          <SectionHead
            icon={Phone} title="Kontak & Sosial"
            onEdit={() => { setDraft(profile); setContactOpen((v) => !v); }}
            editing={contactOpen}
          />
          {!contactOpen ? (
            <div className="grid grid-cols-1 gap-4 text-sm">
              <ReadField label="Nomor HP / WhatsApp" value={profile.phone} icon={Phone} href={waHref} />
              <ReadField label="Email Alternatif" value={profile.alt_email} icon={Mail} href={profile.alt_email ? `mailto:${profile.alt_email}` : null} />
              <ReadField label="LinkedIn" value={profile.linkedin} icon={Linkedin} href={profile.linkedin || null} />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 text-sm">
              <FormField label="Nomor HP / WhatsApp"><Input value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="0812xxx" /></FormField>
              <FormField label="Email Alternatif"><Input type="email" value={draft.alt_email || ""} onChange={(e) => setDraft({ ...draft, alt_email: e.target.value })} /></FormField>
              <FormField label="LinkedIn URL"><Input value={draft.linkedin || ""} onChange={(e) => setDraft({ ...draft, linkedin: e.target.value })} placeholder="https://linkedin.com/in/..." /></FormField>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setContactOpen(false); setDraft(profile); }}>Batal</Button>
                <Button onClick={saveContact} disabled={busy} className="bg-[#090808] hover:bg-[#000000] gap-1.5"><Save className="w-3.5 h-3.5" /> Simpan</Button>
              </div>
            </div>
          )}
        </Card>

        {/* Work Info */}
        <Card className="p-6 border-0 rounded-2xl card-soft lg:col-span-2" data-testid="work-info">
          <SectionHead
            icon={Briefcase} title="Informasi Pekerjaan"
            onEdit={canEditWork ? () => { setDraft(profile); setWorkOpen((v) => !v); } : null}
            editing={workOpen}
            lockReason={!canEditWork ? "Hanya Owner/Director yang bisa mengubah" : ""}
          />
          {!workOpen ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <ReadField label="Jabatan" value={profile.job_title} />
              <ReadField label="Team / Divisi" value={profile.team} />
              <ReadField label="Atasan" value={manager?.name} />
              <ReadField label="Tipe" value={(EMPLOYMENT_OPTIONS.find((e) => e.v === profile.employment_type) || {}).label} />
              <ReadField label="Wilayah Kerja" value={profile.work_area} />
              <ReadField label="Tanggal Bergabung" value={fmtDate(profile.join_date)} />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <FormField label="Jabatan"><Input value={draft.job_title || ""} onChange={(e) => setDraft({ ...draft, job_title: e.target.value })} /></FormField>
              <FormField label="Team / Divisi"><Input value={draft.team || ""} onChange={(e) => setDraft({ ...draft, team: e.target.value })} /></FormField>
              <FormField label="Atasan">
                <Select value={draft.manager_id || "none"} onValueChange={(v) => setDraft({ ...draft, manager_id: v === "none" ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Tidak ada —</SelectItem>
                    {users.filter((u) => u.user_id !== profile.user_id).map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {ROLE_LABEL[u.role]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Tipe">
                <Select value={draft.employment_type || ""} onValueChange={(v) => setDraft({ ...draft, employment_type: v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{EMPLOYMENT_OPTIONS.map((e) => <SelectItem key={e.v} value={e.v}>{e.label}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
              <FormField label="Wilayah Kerja"><Input value={draft.work_area || ""} onChange={(e) => setDraft({ ...draft, work_area: e.target.value })} placeholder="Jakarta / Nasional…" /></FormField>
              <FormField label="Tanggal Bergabung"><DateInput value={draft.join_date || ""} onChange={(e) => setDraft({ ...draft, join_date: e.target.value })} /></FormField>
              <div className="col-span-2 md:col-span-3 flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setWorkOpen(false); setDraft(profile); }}>Batal</Button>
                <Button onClick={saveWork} disabled={busy} className="bg-[#090808] hover:bg-[#000000] gap-1.5"><Save className="w-3.5 h-3.5" /> Simpan</Button>
              </div>
            </div>
          )}
        </Card>

        {!hideCompany && user?.role === "owner" && <CompanySettings />}
      </div>
    </div>
  );
}

function MetaField({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-[#A0A3A8] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">{label}</div>
        <div className="text-sm font-semibold text-[#090808] truncate">{value}</div>
      </div>
    </div>
  );
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-[#090808] transition-colors">{inner}</a>;
  return inner;
}

function SectionHead({ icon: Icon, title, onEdit, editing, lockReason }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-[#DCE0E7] text-[#090808] flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-sm font-bold text-[#090808]">{title}</div>
      </div>
      {onEdit ? (
        <button
          onClick={onEdit}
          data-testid={`edit-btn-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}
          className={`w-8 h-8 rounded-full inline-flex items-center justify-center transition-colors ${
            editing ? "bg-[#090808] text-white" : "bg-[#F4F5F7] hover:bg-[#DCE0E7] text-[#626061] hover:text-[#090808]"
          }`}
          title={editing ? "Tutup" : "Edit"}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      ) : (
        <div className="text-[10px] text-[#A0A3A8]" title={lockReason}>{lockReason}</div>
      )}
    </div>
  );
}

function ReadField({ label, value, icon: Icon, href }) {
  const displayed = value || "—";
  const body = (
    <div className="text-sm font-semibold text-[#090808] break-words">
      {Icon && <Icon className="w-3.5 h-3.5 inline text-[#A0A3A8] mr-1 -mt-0.5" />}
      {displayed}
    </div>
  );
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold mb-0.5">{label}</div>
      {href && value ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#090808] hover:underline break-all">{body}</a>
      ) : body}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, tint = "slate" }) {
  const tints = {
    slate: "bg-[#F4F5F7] text-[#626061]",
    emerald: "bg-emerald-50 text-[#57D28A]",
    sky: "bg-sky-50 text-[#4E6D9A]",
    amber: "bg-amber-50 text-[#B39548]",
    violet: "bg-[#F4F5F7] text-[#090808]",
  };
  return (
    <div className={`rounded-xl p-4 ${tints[tint]}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="w-4 h-4" />
        <div className="text-[10px] uppercase tracking-widest font-bold">{label}</div>
      </div>
      <div className="text-lg font-bold tabular-nums truncate">{value}</div>
      {sub && <div className="text-[11px] opacity-80 truncate mt-0.5">{sub}</div>}
    </div>
  );
}


export function CompanySettingsCard() {
  const [data, setData] = useState({ name: "", logo: "", address: "", email: "", phone: "", web: "" });
  const [draft, setDraft] = useState({ name: "", logo: "", address: "", email: "", phone: "", web: "" });
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings/company").then((r) => {
      const d = r.data || { name: "", logo: "", address: "", email: "", phone: "", web: "" };
      setData(d);
      setDraft(d);
    }).catch(() => {});
  }, []);

  const startEdit = () => { setDraft(data); setEditing(true); };
  const cancelEdit = () => { setDraft(data); setEditing(false); };

  const pickLogo = () => fileRef.current?.click();
  const onFileLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("File harus gambar");
    try {
      setLogoBusy(true);
      const dataUrl = await fileToAvatarDataURL(f);
      setDraft((d) => ({ ...d, logo: dataUrl }));
      toast.success("Logo dipilih — klik Simpan untuk menerapkan");
    } catch { toast.error("Gagal memproses"); }
    finally { setLogoBusy(false); }
  };
  const removeLogo = () => setDraft((d) => ({ ...d, logo: "" }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/settings/company", {
        name: draft.name || "",
        logo: draft.logo || "",
        address: draft.address || "",
        email: draft.email || "",
        phone: draft.phone || "",
        web: draft.web || "",
        invoice_prefix: draft.invoice_prefix || "",
        invoice_prefix_non_ppn: draft.invoice_prefix_non_ppn || "",
        invoice_due_days: draft.invoice_due_days ?? 7,
        bank_ppn_name: draft.bank_ppn_name || "",
        bank_ppn_account: draft.bank_ppn_account || "",
        bank_ppn_holder: draft.bank_ppn_holder || "",
        bank_nonppn_name: draft.bank_nonppn_name || "",
        bank_nonppn_account: draft.bank_nonppn_account || "",
        bank_nonppn_holder: draft.bank_nonppn_holder || "",
        npwp_ppn_name: draft.npwp_ppn_name || "",
        npwp_ppn_number: draft.npwp_ppn_number || "",
        npwp_ppn_address: draft.npwp_ppn_address || "",
        npwp_nonppn_number: draft.npwp_nonppn_number || "",
        npwp_nonppn_name: draft.npwp_nonppn_name || "",
        npwp_nonppn_nik: draft.npwp_nonppn_nik || "",
        npwp_nonppn_address: draft.npwp_nonppn_address || "",
      });
      setData(r.data);
      setDraft(r.data);
      setEditing(false);
      toast.success("Pengaturan perusahaan disimpan");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const view = editing ? draft : data;

  return (
    <Card className="p-6 border-0 rounded-2xl card-soft" data-testid="company-settings-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[#DCE0E7] text-[#090808] flex items-center justify-center">
            <Building2 className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-[#090808]">Pengaturan Perusahaan</h2>
            <p className="text-[11px] text-[#A0A3A8] mt-0.5">Logo, nama, alamat & kontak untuk header PDF report.</p>
          </div>
        </div>
        {!editing ? (
          <button
            onClick={startEdit}
            data-testid="company-edit-toggle"
            className="w-8 h-8 rounded-full inline-flex items-center justify-center transition-colors bg-[#F4F5F7] hover:bg-[#DCE0E7] text-[#626061] hover:text-[#090808]"
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={cancelEdit} disabled={busy} data-testid="company-edit-cancel" className="h-8 rounded-full">Batal</Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="company-settings-save-btn" className="bg-[#090808] hover:bg-[#000000] gap-1.5 h-8 rounded-full">
              <Save className="w-3.5 h-3.5" /> {busy ? "Menyimpan…" : "Simpan"}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
        {/* Logo panel */}
        <div className="md:col-span-1 flex flex-col items-center gap-3 p-4 bg-gradient-to-br from-[#F5F3FF] to-[#F4F5F7] rounded-2xl border border-[#DCE0E7]">
          {view.logo ? (
            <img src={view.logo} alt="Logo" className="w-32 h-32 rounded-2xl object-contain bg-white border border-[#DCE0E7]" />
          ) : (
            <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-[#DCE0E7] to-white text-[#090808] font-bold text-xs flex items-center justify-center border border-[#DCE0E7]">
              <Building2 className="w-10 h-10" />
            </div>
          )}
          {editing && (
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={pickLogo} disabled={logoBusy} data-testid="company-logo-upload-btn" className="bg-[#090808] hover:bg-[#000000] gap-1.5 h-8">
                <ImagePlus className="w-3.5 h-3.5" /> {view.logo ? "Ganti" : "Upload"}
              </Button>
              {view.logo && (
                <button onClick={removeLogo} data-testid="company-logo-remove-btn" className="inline-flex items-center gap-1 text-[11px] text-[#A0A3A8] hover:text-[#FF6B7D] h-8 px-2 rounded-lg">
                  <X className="w-3 h-3" /> Hapus
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileLogo} data-testid="company-logo-file-input" />
            </div>
          )}
        </div>

        {/* Fields */}
        <div className="md:col-span-2 space-y-4">
          {!editing ? (
            <>
              <ReadField label="Nama Perusahaan" value={view.name} />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold mb-0.5">Alamat</div>
                <div className="text-sm font-semibold text-[#090808] whitespace-pre-wrap break-words">
                  {view.address || "—"}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ReadField label="Email" value={view.email} icon={Mail} href={view.email ? `mailto:${view.email}` : null} />
                <ReadField label="Phone" value={view.phone} icon={Phone} />
                <ReadField label="Website" value={view.web} icon={Globe} href={view.web ? (view.web.startsWith("http") ? view.web : `https://${view.web}`) : null} />
              </div>
              <InvoiceSettingsBlock view={view} />
            </>
          ) : (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Nama Perusahaan</Label>
                <Input
                  data-testid="company-name-input"
                  value={draft.name || ""}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="PT ABC Sejahtera"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Alamat</Label>
                <textarea
                  data-testid="company-address-input"
                  value={draft.address || ""}
                  onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                  placeholder="Jl. Contoh No. 123, Jakarta 12345"
                  rows={3}
                  maxLength={500}
                  className="mt-1.5 w-full rounded-xl border border-[#DCE0E7] bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black/10"
                />
                <div className="text-[10px] text-[#A0A3A8] mt-1 text-right">{(draft.address || "").length}/500</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Kontak</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-1.5">
                  <Input
                    data-testid="company-email-input"
                    type="email"
                    value={draft.email || ""}
                    onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                    placeholder="Email"
                    maxLength={120}
                  />
                  <Input
                    data-testid="company-phone-input"
                    value={draft.phone || ""}
                    onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                    placeholder="Phone"
                    maxLength={60}
                  />
                  <Input
                    data-testid="company-web-input"
                    value={draft.web || ""}
                    onChange={(e) => setDraft({ ...draft, web: e.target.value })}
                    placeholder="Web"
                    maxLength={200}
                  />
                </div>
              </div>
              <InvoiceSettingsEdit draft={draft} setDraft={setDraft} />
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function BankRead({ title, hint, name, account, holder, testid }) {
  return (
    <div className="rounded-xl border border-[#DCE0E7] bg-[#FAFBFD] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">{title}</div>
      <div className="text-[10px] text-[#A0A3A8] mb-2">{hint}</div>
      <div className="text-sm font-semibold text-[#090808]">{name || "—"}</div>
      <div className="text-sm font-bold text-[#090808] tabular-nums">{account || "—"}</div>
      <div className="text-xs text-[#626061]">a.n. {holder || "—"}</div>
    </div>
  );
}

function InvoiceSettingsBlock({ view }) {
  return (
    <div className="pt-4 border-t border-[#DCE0E7] space-y-3" data-testid="company-invoice-settings-read">
      <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Invoice</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ReadField label="Seri Invoice PPN" value={view.invoice_prefix} />
        <ReadField label="Seri Invoice Non-PPN" value={view.invoice_prefix_non_ppn} />
        <ReadField label="Jatuh Tempo Default" value={view.invoice_due_days != null ? `${view.invoice_due_days} hari` : null} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <BankRead
          title="Rekening Invoice PPN"
          hint="Rekening perusahaan (PT)"
          name={view.bank_ppn_name} account={view.bank_ppn_account} holder={view.bank_ppn_holder}
          testid="company-bank-ppn-read"
        />
        <BankRead
          title="Rekening Invoice Non-PPN"
          hint="Rekening pribadi (Owner)"
          name={view.bank_nonppn_name} account={view.bank_nonppn_account} holder={view.bank_nonppn_holder}
          testid="company-bank-nonppn-read"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-[#DCE0E7] bg-[#FAFBFD] p-3" data-testid="company-npwp-ppn-read">
          <div className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">NPWP Invoice PPN</div>
          <div className="text-[10px] text-[#A0A3A8] mb-2">NPWP perusahaan (PT)</div>
          <div className="text-sm font-semibold text-[#090808]">{view.npwp_ppn_name || "—"}</div>
          <div className="text-sm font-bold text-[#090808] tabular-nums">{view.npwp_ppn_number || "—"}</div>
          <div className="text-xs text-[#626061] whitespace-pre-wrap">{view.npwp_ppn_address || "—"}</div>
        </div>
        <div className="rounded-xl border border-[#DCE0E7] bg-[#FAFBFD] p-3" data-testid="company-npwp-nonppn-read">
          <div className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">NPWP Invoice Non-PPN</div>
          <div className="text-[10px] text-[#A0A3A8] mb-2">NPWP pribadi (Owner)</div>
          <div className="text-sm font-bold text-[#090808] tabular-nums">{view.npwp_nonppn_number || "—"}</div>
          <div className="text-sm font-semibold text-[#090808]">{view.npwp_nonppn_name || "—"}</div>
          <div className="text-xs text-[#626061]">NIK: {view.npwp_nonppn_nik || "—"}</div>
          <div className="text-xs text-[#626061] whitespace-pre-wrap mt-1">{view.npwp_nonppn_address || "—"}</div>
        </div>
      </div>
    </div>
  );
}

function BankEdit({ title, hint, prefix, draft, setDraft }) {
  return (
    <div className="rounded-xl border border-[#DCE0E7] bg-white p-3 space-y-2">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">{title}</div>
        <div className="text-[10px] text-[#A0A3A8]">{hint}</div>
      </div>
      <Input
        data-testid={`company-${prefix}-name-input`}
        value={draft[`bank_${prefix}_name`] || ""}
        onChange={(e) => setDraft({ ...draft, [`bank_${prefix}_name`]: e.target.value })}
        placeholder="Nama bank (mis. BCA)"
        maxLength={80}
      />
      <Input
        data-testid={`company-${prefix}-account-input`}
        value={draft[`bank_${prefix}_account`] || ""}
        onChange={(e) => setDraft({ ...draft, [`bank_${prefix}_account`]: e.target.value })}
        placeholder="No. rekening"
        maxLength={60}
      />
      <Input
        data-testid={`company-${prefix}-holder-input`}
        value={draft[`bank_${prefix}_holder`] || ""}
        onChange={(e) => setDraft({ ...draft, [`bank_${prefix}_holder`]: e.target.value })}
        placeholder="Atas nama"
        maxLength={120}
      />
    </div>
  );
}

function InvoiceSettingsEdit({ draft, setDraft }) {
  return (
    <div className="pt-4 border-t border-[#DCE0E7] space-y-3" data-testid="company-invoice-settings-edit">
      <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Invoice</Label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input
          data-testid="company-invoice-prefix-input"
          value={draft.invoice_prefix || ""}
          onChange={(e) => setDraft({ ...draft, invoice_prefix: e.target.value })}
          placeholder="Seri PPN (INV-IDS)"
          maxLength={40}
        />
        <Input
          data-testid="company-invoice-prefix-nonppn-input"
          value={draft.invoice_prefix_non_ppn || ""}
          onChange={(e) => setDraft({ ...draft, invoice_prefix_non_ppn: e.target.value })}
          placeholder="Seri Non-PPN (P-INV-IDS)"
          maxLength={40}
        />
        <Input
          data-testid="company-invoice-due-days-input"
          type="number"
          min="0"
          value={draft.invoice_due_days ?? 7}
          onChange={(e) => setDraft({ ...draft, invoice_due_days: e.target.value })}
          placeholder="Jatuh tempo (hari)"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <BankEdit title="Rekening Invoice PPN" hint="Rekening perusahaan (PT)" prefix="ppn" draft={draft} setDraft={setDraft} />
        <BankEdit title="Rekening Invoice Non-PPN" hint="Rekening pribadi (Owner)" prefix="nonppn" draft={draft} setDraft={setDraft} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-[#DCE0E7] bg-white p-3 space-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">NPWP Invoice PPN</div>
            <div className="text-[10px] text-[#A0A3A8]">NPWP perusahaan (PT)</div>
          </div>
          <Input
            data-testid="company-npwp-ppn-name-input"
            value={draft.npwp_ppn_name || ""}
            onChange={(e) => setDraft({ ...draft, npwp_ppn_name: e.target.value })}
            placeholder="Nama perusahaan pada NPWP"
            maxLength={200}
          />
          <Input
            data-testid="company-npwp-ppn-number-input"
            value={draft.npwp_ppn_number || ""}
            onChange={(e) => setDraft({ ...draft, npwp_ppn_number: e.target.value })}
            placeholder="Nomor NPWP"
            maxLength={60}
          />
          <textarea
            data-testid="company-npwp-ppn-address-input"
            value={draft.npwp_ppn_address || ""}
            onChange={(e) => setDraft({ ...draft, npwp_ppn_address: e.target.value })}
            placeholder="Alamat pada NPWP"
            rows={2}
            maxLength={500}
            className="w-full rounded-xl border border-[#DCE0E7] bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black/10"
          />
        </div>
        <div className="rounded-xl border border-[#DCE0E7] bg-white p-3 space-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">NPWP Invoice Non-PPN</div>
            <div className="text-[10px] text-[#A0A3A8]">NPWP pribadi (Owner)</div>
          </div>
          <Input
            data-testid="company-npwp-nonppn-number-input"
            value={draft.npwp_nonppn_number || ""}
            onChange={(e) => setDraft({ ...draft, npwp_nonppn_number: e.target.value })}
            placeholder="Nomor NPWP"
            maxLength={60}
          />
          <Input
            data-testid="company-npwp-nonppn-name-input"
            value={draft.npwp_nonppn_name || ""}
            onChange={(e) => setDraft({ ...draft, npwp_nonppn_name: e.target.value })}
            placeholder="Nama sesuai NPWP"
            maxLength={150}
          />
          <Input
            data-testid="company-npwp-nonppn-nik-input"
            value={draft.npwp_nonppn_nik || ""}
            onChange={(e) => setDraft({ ...draft, npwp_nonppn_nik: e.target.value })}
            placeholder="NIK"
            maxLength={40}
          />
          <textarea
            data-testid="company-npwp-nonppn-address-input"
            value={draft.npwp_nonppn_address || ""}
            onChange={(e) => setDraft({ ...draft, npwp_nonppn_address: e.target.value })}
            placeholder="Alamat pada NPWP"
            rows={2}
            maxLength={500}
            className="w-full rounded-xl border border-[#DCE0E7] bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black/10"
          />
        </div>
      </div>
    </div>
  );
}
